<template>
  <div class="toast-container">
    <TransitionGroup
      name="toast"
      tag="div" 
      class="toast-list"
      appear
    >
      <div
        v-for="toast in toasts"
        :key="`toast-${toast.id}`"
        :class="['toast', `toast--${toast.type}`]"
        @click="removeToast(toast.id)"
      >
        <div class="toast__icon">
          <!-- Success Icon -->
          <svg v-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7.5 13.475L3.525 9.5L2.1 10.925L7.5 16.325L17.9 5.925L16.475 4.5L7.5 13.475Z"/>
          </svg>
          <!-- Error Icon -->
          <svg v-else-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"/>
          </svg>
          <!-- Warning Icon -->
          <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0L20 18H0L10 0ZM9 7V11H11V7H9ZM9 13V15H11V13H9Z"/>
          </svg>
          <!-- Info Icon -->
          <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z"/>
          </svg>
        </div>
        <div class="toast__content">
          <h4 class="toast__title">{{ toast.title }}</h4>
          <p v-if="toast.message" class="toast__message">{{ toast.message }}</p>
        </div>
        <button class="toast__close" @click.stop="removeToast(toast.id)">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { useToast } from './useToast'

const { toasts, removeToast } = useToast()
</script>

<script>
export default {
  name: 'ToastNotification'
}
</script>

<style>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  pointer-events: none;
}

.toast-list {
  display: flex;
  flex-direction: column;
}

.toast {
  position: relative;
  display: flex !important;
  align-items: flex-start;
  gap: 12px;
  min-width: 300px;
  max-width: 400px;
  margin-bottom: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  cursor: pointer;
  pointer-events: all;
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: 1 !important;
  visibility: visible !important;
  
  /* Dark glassmorphism background */
  background: rgba(30, 41, 59, 0.85) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.1);
}

/* Left gradient accent bar */
.toast::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 50%; /* Quarter of the toast width */
  border-radius: 12px 0 0 12px;
  opacity: 0.3; /* For subtle fading effect */
}

/* Success Toast */
.toast--success {
  color: rgba(255, 255, 255, 0.95) !important;
}

.toast--success::before {
  background: linear-gradient(90deg, #10b981 0%, rgba(16, 185, 129, 0.6) 70%, rgba(16, 185, 129, 0) 100%);
}

.toast--success .toast__icon {
  color: #10b981 !important;
}

/* Error Toast */
.toast--error {
  color: rgba(255, 255, 255, 0.95) !important;
}

.toast--error::before {
  background: linear-gradient(90deg, #ef4444 0%, rgba(239, 68, 68, 0.6) 70%, rgba(239, 68, 68, 0) 100%);
}

.toast--error .toast__icon {
  color: #ef4444 !important;
}

/* Warning Toast */
.toast--warning {
  color: rgba(255, 255, 255, 0.95) !important;
}

.toast--warning::before {
  background: linear-gradient(90deg, #f59e0b 0%, rgba(245, 158, 11, 0.6) 70%, rgba(245, 158, 11, 0) 100%);
}

.toast--warning .toast__icon {
  color: #f59e0b !important;
}

/* Info Toast */
.toast--info {
  color: rgba(255, 255, 255, 0.95) !important;
}

.toast--info::before {
  background: linear-gradient(90deg, #3b82f6 0%, rgba(59, 130, 246, 0.6) 70%, rgba(59, 130, 246, 0) 100%);
}

.toast--info .toast__icon {
  color: #3b82f6 !important;
}

.toast__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.toast__content {
  flex: 1;
  min-width: 0;
}

.toast__title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: inherit;
}

.toast__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.85;
  color: inherit;
}

.toast__close {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s ease;
}

.toast__close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

/* Hover effect */
.toast:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

/* Animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.5s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.5s ease;
}

@media (max-width: 640px) {
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
  }
  
  .toast {
    min-width: unset;
    max-width: unset;
    margin-right: 0;
    margin-left: 0;
  }
}

/* Dark mode support (optional) */
@media (prefers-color-scheme: dark) {
  .toast {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  }
  
  .toast--success {
    background: rgba(16, 185, 129, 0.2);
    border: 1px solid rgba(16, 185, 129, 0.4);
  }
  
  .toast--error {
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.4);
  }
  
  .toast--warning {
    background: rgba(245, 158, 11, 0.2);
    border: 1px solid rgba(245, 158, 11, 0.4);
  }
  
  .toast--info {
    background: rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.4);
  }
  
  .toast__close {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .toast__close:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>